import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const nodes = [
    { nx: 104, ny: 86,  lbl: 'YouTube', clicks: '1,240', grad: 'lk-yt', border: '#ef4444', text: '#dc2626' },
    { nx: 230, ny: 62,  lbl: 'Blog',    clicks: '3,820', grad: 'lk-bl', border: '#16a34a', text: '#15803d' },
    { nx: 356, ny: 86,  lbl: 'TikTok',  clicks: '2,150', grad: 'lk-tt', border: '#a21caf', text: '#9333ea' },
    { nx: 356, ny: 212, lbl: 'Twitter', clicks:   '890', grad: 'lk-tw', border: '#0284c7', text: '#0369a1' },
    { nx: 104, ny: 212, lbl: 'Email',   clicks: '1,640', grad: 'lk-em', border: '#4f46e5', text: '#4338ca' },
];

export default function IlluLinks() {
    const svgRef = useRef(null);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;

        const ctx = gsap.context(() => {
            const hub     = svg.querySelector('#lk-hub');
            const hubRing = svg.querySelector('#lk-hub-ring');
            const lines   = Array.from(svg.querySelectorAll('.lk-line'));
            const nodeEls = Array.from(svg.querySelectorAll('.lk-node'));
            const footer  = svg.querySelector('#lk-footer');

            // ── initial states ──
            gsap.set(hub,     { opacity: 0, scale: 0.6, svgOrigin: '230 148' });
            gsap.set(hubRing, { attr: { r: 22 }, opacity: 0 });
            gsap.set(footer,  { opacity: 0 });

            // Collapse every line to a zero-length point at the hub.
            // Zero-length geometry is invisible unconditionally — no dependency on CSS paint order.
            lines.forEach((line, i) => {
                gsap.set(line, { attr: { x2: 230, y2: 148 }, opacity: 1 });
                gsap.set(nodeEls[i], {
                    opacity: 0,
                    scale: 0,
                    svgOrigin: `${nodes[i].nx} ${nodes[i].ny}`,
                });
            });

            // ── entry timeline ──
            const tl = gsap.timeline({
                scrollTrigger: { trigger: svg, start: 'top 85%', once: true },
                onComplete: startLoops,
            });

            tl.to(hub, { opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(2.2)' });

            // Each line grows from hub outward showing dashes, then its node pops in
            lines.forEach((line, i) => {
                tl
                    .to(line, {
                        attr: { x2: nodes[i].nx, y2: nodes[i].ny },
                        duration: 0.42,
                        ease: 'power2.inOut',
                    })
                    .to(nodeEls[i], {
                        opacity: 1,
                        scale: 1,
                        duration: 0.32,
                        ease: 'back.out(2.8)',
                    }, '-=0.05');
            });

            tl.to(footer, { opacity: 1, duration: 0.35 }, '-=0.1');

            // ── loop animations ──
            function startLoops() {
                // Dashes march — strokeDashoffset is a CSS property, GPU composited
                lines.forEach(line => {
                    gsap.to(line, { strokeDashoffset: -9, duration: 0.5, ease: 'none', repeat: -1 });
                });

                // Hub pulse ring
                gsap.fromTo(hubRing,
                    { attr: { r: 22 }, opacity: 0.55 },
                    { attr: { r: 50 }, opacity: 0, duration: 1.6, ease: 'sine.out', repeat: -1, repeatDelay: 0.9 }
                );

                // Hub gentle breathe
                gsap.to(hub, {
                    scale: 1.04, svgOrigin: '230 148',
                    duration: 1.8, ease: 'sine.inOut', yoyo: true, repeat: -1,
                });

                // Nodes stay at opacity 1 — no animation on filter elements avoids repaint
            }
        }, svgRef);

        return () => ctx.revert();
    }, []);

    return (
        <svg ref={svgRef} viewBox="0 0 460 296" fill="none" xmlns="http://www.w3.org/2000/svg" className="pxl-al-illu-svg">
            <defs>
                <linearGradient id="lk-bg" x1="0" y1="0" x2="1" y2="1">
                    <stop stopColor="#f0eaff"/><stop offset="1" stopColor="#e6deff"/>
                </linearGradient>
                <linearGradient id="lk-hub-grad" x1="0" y1="0" x2="1" y2="0">
                    <stop stopColor="#7c3aed"/><stop offset="1" stopColor="#0d9488"/>
                </linearGradient>
                <linearGradient id="lk-yt" x1="0" y1="0" x2="0" y2="1">
                    <stop stopColor="#fee2e2"/><stop offset="1" stopColor="#fecaca"/>
                </linearGradient>
                <linearGradient id="lk-bl" x1="0" y1="0" x2="0" y2="1">
                    <stop stopColor="#dcfce7"/><stop offset="1" stopColor="#bbf7d0"/>
                </linearGradient>
                <linearGradient id="lk-tt" x1="0" y1="0" x2="0" y2="1">
                    <stop stopColor="#f3e8ff"/><stop offset="1" stopColor="#e9d5ff"/>
                </linearGradient>
                <linearGradient id="lk-tw" x1="0" y1="0" x2="0" y2="1">
                    <stop stopColor="#e0f2fe"/><stop offset="1" stopColor="#bae6fd"/>
                </linearGradient>
                <linearGradient id="lk-em" x1="0" y1="0" x2="0" y2="1">
                    <stop stopColor="#e0e7ff"/><stop offset="1" stopColor="#c7d2fe"/>
                </linearGradient>
                <filter id="lk-shadow" x="-5%" y="-10%" width="110%" height="130%">
                    <feDropShadow dx="0" dy="6" stdDeviation="14" floodColor="#4c1d95" floodOpacity="0.09"/>
                </filter>
                <filter id="lk-node" x="-14%" y="-26%" width="128%" height="162%">
                    <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor="#000" floodOpacity="0.10"/>
                </filter>
                <clipPath id="lk-card-clip">
                    <rect x="18" y="14" width="424" height="268" rx="18"/>
                </clipPath>
            </defs>

            <rect width="460" height="296" fill="url(#lk-bg)"/>
            <rect x="18" y="14" width="424" height="268" rx="18" fill="#fff" filter="url(#lk-shadow)"/>

            <g clipPath="url(#lk-card-clip)">

                {/* Lines drawn first so nodes (painted after) cover them with solid fills */}
                {nodes.map(({ nx, ny, border }, i) => (
                    <line key={i} className="lk-line"
                        x1="230" y1="148" x2={nx} y2={ny}
                        stroke={border} strokeWidth="1.8" strokeDasharray="5 4"/>
                ))}

                {/* Hub pulse ring */}
                <circle id="lk-hub-ring" cx="230" cy="148" r="22"
                    fill="none" stroke="#7c3aed" strokeWidth="1.5" opacity="0"/>

                {/* Central hub */}
                <g id="lk-hub">
                    <rect x="118" y="128" width="224" height="40" rx="20" fill="url(#lk-hub-grad)"/>
                    <text x="230" y="154" textAnchor="middle" fontSize="12" fill="#fff"
                        fontWeight="700" fontFamily="inherit" letterSpacing="0.3">
                        pixalab.com/ref/you
                    </text>
                </g>

                {/* Nodes drawn after lines — solid fills paint over any line that passes underneath */}
                {nodes.map(({ nx, ny, lbl, clicks, grad, border, text }, i) => (
                    <g key={lbl} className="lk-node">
                        <g filter="url(#lk-node)">
                            <rect x={nx - 52} y={ny - 22} width="104" height="44" rx="14"
                                fill={`url(#${grad})`} stroke={border} strokeWidth="1.8"/>
                            <text x={nx} y={ny - 4}  textAnchor="middle" fontSize="11.5"
                                fill={text} fontWeight="800" fontFamily="inherit">{lbl}</text>
                            <text x={nx} y={ny + 13} textAnchor="middle" fontSize="9.5"
                                fill={text} opacity="0.75" fontFamily="inherit">{clicks} clicks</text>
                        </g>
                    </g>
                ))}

                {/* Footer */}
                <line x1="50" y1="278" x2="410" y2="278" stroke="#e8eaf6" strokeWidth="1"/>
                <text id="lk-footer" x="230" y="290" textAnchor="middle" fontSize="9.5"
                    fill="#7986cb" fontWeight="500" fontFamily="inherit">
                    One link · every channel tracked independently
                </text>

            </g>
        </svg>
    );
}
